import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import type { CapacityStatus } from '@/components/chat/types/types';
import { cn } from '@/lib/cn';
import { AlertTriangle, Info, Loader2, MessageSquarePlus, X } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface ChatCapacityBannerProps {
  readonly capacityStatus: CapacityStatus;
  readonly onNewChat: () => void;
  readonly isMigrating?: boolean;
  readonly className?: string;
}

const BANNER_CONFIG = {
  warning: {
    Icon: Info,
    containerClass:
      'border-border bg-surface-accent text-text-secondary',
    iconClass: 'text-text-secondary',
    dismissable: true,
  },
  critical: {
    Icon: AlertTriangle,
    containerClass:
      'border-border bg-surface-accent text-text-secondary',
    iconClass: 'text-text-secondary',
    dismissable: false,
  },
  exceeded: {
    Icon: AlertTriangle,
    containerClass:
      'border-border bg-surface-accent text-text-secondary',
    iconClass: 'text-text-secondary',
    dismissable: false,
  },
} as const;

export function ChatCapacityBanner({
  capacityStatus,
  onNewChat,
  isMigrating = false,
  className,
}: ChatCapacityBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  if (capacityStatus === 'ok' || (isDismissed && capacityStatus === 'warning')) {
    return null;
  }

  const config = BANNER_CONFIG[capacityStatus];
  const { Icon, containerClass, iconClass, dismissable } = config;

  return (
    <div
      role="alert"
      data-testid="chat-capacity-banner"
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border px-4 py-3',
        containerClass,
        className
      )}
    >
      <Icon className={cn('size-4 shrink-0', iconClass)} />

      <div className="flex min-w-0 flex-1 flex-col">
        {capacityStatus === 'warning' && (
          <TypographyCaption>
            <FormattedMessage
              defaultMessage="This conversation is getting long. Consider starting a new chat for best performance."
              id="chatCapacity.warning"
            />
          </TypographyCaption>
        )}

        {capacityStatus === 'critical' && (
          <TypographyCaption>
            <FormattedMessage
              defaultMessage="This chat has grown large. Migrating your chat will give you faster, more accurate responses."
              id="chatCapacity.critical"
            />
          </TypographyCaption>
        )}

        {capacityStatus === 'exceeded' && (
          <TypographyCaption>
            <FormattedMessage
              defaultMessage="This chat can no longer accept messages. Migrate to continue your conversation."
              id="chatCapacity.exceeded"
            />
          </TypographyCaption>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {(capacityStatus === 'critical' || capacityStatus === 'exceeded') && (
          <Button
            variant="default"
            size="xs"
            onClick={onNewChat}
            disabled={isMigrating}
            type="button"
          >
            {isMigrating ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <MessageSquarePlus className="size-3.5" />
            )}
            <FormattedMessage
              defaultMessage="Migrate conversation"
              id="chatCapacity.migrate"
            />
          </Button>
        )}

        {dismissable && (
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            aria-label="Dismiss"
            onClick={() => setIsDismissed(true)}
            type="button"
          >
            <X size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}
